import { useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@localagent/ui';

export function NewFileAction({ onCreateFile }: { onCreateFile: (name: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();
  const invalid = trimmed.length === 0 || trimmed.includes('/');

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setName('');
      setError(null);
      setPending(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (invalid || pending) return;
    setPending(true);
    setError(null);
    try {
      await onCreateFile(trimmed);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <button
        type="button"
        data-testid="new-file-button"
        aria-label="New file"
        title="New file"
        onClick={() => setOpen(true)}
        className="rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0"
        >
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
          <path d="M12 18v-6" />
          <path d="M9 15h6" />
        </svg>
      </button>
      <DialogContent data-testid="new-file-dialog">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New file</DialogTitle>
            <DialogDescription>
              Creates the file at the workspace root and opens it in the editor.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="new-file-name">File name</Label>
            <Input
              id="new-file-name"
              data-testid="new-file-name"
              autoFocus
              placeholder="e.g. notes.md"
              value={name}
              disabled={pending}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {error !== null && (
            <p className="text-body text-destructive" role="alert" data-testid="new-file-error">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={invalid || pending} data-testid="new-file-confirm">
              {pending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
